import './styles/global.less';
import './styles/global.scss';
import './styles/mobile-global.scss';
import { Auth0Provider } from '@auth0/auth0-react';
import { NewRelicProvider } from '@contexts/NewRelicContext';
import { AgentOptions } from '@newrelic/browser-agent/loaders/agent';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_CONNECTION,
  AUTH0_DOMAIN,
  BASE_URL,
  DOMAIN,
  ENVIRONMENT,
  G_ANALYTICS_ID,
  NEW_RELIC_BEACON,
  NEW_RELIC_APP_ID,
  NEW_RELIC_ACCOUNT_ID,
  NEW_RELIC_LICENSE_KEY,
  applyRuntimeFrontendConfig,
  FrontendRuntimeConfig,
} from '@shared/frontendEnv';
import axios from 'axios';
import * as ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import Router from './Router';
import { TranslationProvider } from './TranslationContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import WebProvider from './providers/WebProvider';

axios.defaults.baseURL = BASE_URL;

const loadEnv = async (): Promise<Partial<FrontendRuntimeConfig>> => {
  const response = await fetch('/config.json');
  if (!response.ok) {
    throw new Error(`Failed to load environment variables: ${response.statusText}`);
  }
  const env = (await response.json()) as FrontendRuntimeConfig;

  window.env = env;

  return env;
};

const initApp = async () => {
  try {
    const runtimeConfig = await loadEnv();
    applyRuntimeFrontendConfig(runtimeConfig);
  } catch (error) {
    // Fall back silently to compile-time environment values.
  }

  axios.defaults.baseURL = BASE_URL;

  if (ENVIRONMENT === 'production') {
    console.log = function () {};
    console.warn = function () {};
    console.error = function () {};
    console.info = function () {};
    console.debug = function () {};
  }

  const redirect_uri = `${BASE_URL}/home`;
  const normalizedEnvironment = ENVIRONMENT.toLowerCase();

  const newRelicAgent = new BrowserAgent({
    info: {
      applicationID: NEW_RELIC_APP_ID ?? '',
      beacon: NEW_RELIC_BEACON ?? '',
      errorBeacon: NEW_RELIC_BEACON ?? '',
      licenseKey: NEW_RELIC_LICENSE_KEY ?? '',
      sa: 1,
    },
    init: {
      distributed_tracing: {
        allowed_origins: [],
        cors_enabled: false,
        cors_use_newrelic_header: false,
        cors_use_tracecontext_headers: false,
        enabled: true,
        exclude_newrelic_header: false,
      },
      privacy: {
        cookies_enabled: true,
      },
      session_replay: {
        autoStart: true,
        block_selector: '',
        collect_fonts: true,
        enabled: true,
        error_sampling_rate: 100,
        fix_stylesheets: true,
        inline_images: false,
        inline_stylesheet: true,
        mask_all_inputs: false,
        mask_input_options: {},
        mask_text_selector: '',
        sampling_rate: 0.000001,
      },
    },
    loader_config: {
      accountID: NEW_RELIC_ACCOUNT_ID ?? '',
      agentID: NEW_RELIC_APP_ID ?? '',
      applicationID: NEW_RELIC_APP_ID ?? '',
      licenseKey: NEW_RELIC_LICENSE_KEY ?? '',
      trustKey: NEW_RELIC_ACCOUNT_ID ?? '',
    },
  } as AgentOptions);

  newRelicAgent.setCustomAttribute('environment', normalizedEnvironment, true);
  newRelicAgent.setCustomAttribute('stack', 'agora', true);
  newRelicAgent.setCustomAttribute('domain', DOMAIN, true);

  ReactDOM.render(
    <Auth0Provider
      domain={AUTH0_DOMAIN ?? ''}
      clientId={AUTH0_CLIENT_ID ?? ''}
      authorizationParams={{
        redirect_uri,
        audience: AUTH0_AUDIENCE ?? '',
        connection: AUTH0_CONNECTION ?? '',
        scope: 'openid profile email offline_access',
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <TranslationProvider>
        <WebProvider environment={ENVIRONMENT}>
          <NewRelicProvider newRelicAgent={newRelicAgent}>
            <AnalyticsProvider analyticsId={G_ANALYTICS_ID ?? ''}>
              <BrowserRouter
                getUserConfirmation={() => {
                  /* Empty callback to block the default browser prompt */
                }}
              >
                <Router />
              </BrowserRouter>
            </AnalyticsProvider>
          </NewRelicProvider>
        </WebProvider>
      </TranslationProvider>
    </Auth0Provider>,
    document.getElementById('root')
  );
};

initApp();
